import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabase";
import { formatCurrency } from "../../utils/helpers";
import { BRANDS } from "../../utils/constants";
import useCSVProcessor from "../../hooks/useCSVProcessor";
import useSupabaseData from "../../hooks/useSupabaseData";
import Card from "../ui/Card";
import Button from "../ui/Button";
import FileUploader from "../FileUploader";
import WhitelistManager from "./WhitelistManager";
import ReferralManager from "./ReferralManager";
import ModalSettings from "./ModalSettings";
import ProgressBar from "../ui/ProgressBar";
import {
  Settings,
  Trash2,
  Upload,
  Database,
  Filter,
  Tag,
  Info,
  AlertTriangle,
  CheckCircle,
  UploadCloud,
  Cog,
  Zap,
  Users,
  UserPlus,
  Calendar,
  MessageSquare,
  DollarSign,
  Wallet,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../Header";
import DeleteDateModal from "./DeleteDateModal";
import PayoutRequesters from "./PayoutRequesters";
import PayoutDashboard from "./PayoutDashboard";
import { dateDeleteService } from "../../lib/dateDeleteService";

const AdminSettingsPage = () => {
  const { isAdmin, isRoleLoading } = useAuth();
  const navigate = useNavigate();
  const [commissionPercentage, setCommissionPercentage] = useState(4.8);
  const [dataOverview, setDataOverview] = useState({
    totalRecords: 0,
    totalAffiliates: 0,
    totalSpend: 0,
    totalRevenue: 0,
    dateRange: { earliest: null, latest: null },
    isLoading: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [activeTab, setActiveTab] = useState("upload");

  // Date deletion state
  const [selectedDate, setSelectedDate] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [isLoadingDates, setIsLoadingDates] = useState(false);

  // CSV Upload functionality
  const {
    isLoading: supabaseLoading,
    error: supabaseError,
    success: supabaseSuccess,
    importProgress,
    importMessage,
    importCSVDataInNewDashboard,
    clearMessages: clearSupabaseMessages,
  } = useSupabaseData();

  const {
    isLoading: csvLoading,
    error: csvError,
    success: csvSuccess,
    processFile,
    clearMessages,
  } = useCSVProcessor();

  const [brandForCSVUpload, setBrandForCSVUpload] = useState("none");
  const [uploadInitiated, setUploadInitiated] = useState(false);

  // Load data overview from database
  const loadDataOverview = async () => {
    try {
      console.log("🔍 Loading data overview...");

      // Get total records, spend, revenue, and date range
      const { data: performanceData, error: performanceError } = await supabase
        .from("ad_performance")
        .select("spend, revenue, date, affiliate_name")
        .gt("spend", 0);

      if (performanceError) throw performanceError;

      // Get unique affiliates count
      const { data: affiliateData, error: affiliateError } = await supabase
        .from("ad_performance")
        .select("affiliate_name")
        .gt("spend", 0);

      if (affiliateError) throw affiliateError;

      const uniqueAffiliates = new Set(
        affiliateData.map((row) => row.affiliate_name)
      ).size;

      // Calculate totals and date range
      const totalRecords = performanceData.length;
      const totalSpend = performanceData.reduce(
        (sum, row) => sum + (parseFloat(row.spend) || 0),
        0
      );
      const totalRevenue = performanceData.reduce(
        (sum, row) => sum + (parseFloat(row.revenue) || 0),
        0
      );

      // Find date range
      const dates = performanceData
        .map((row) => row.date)
        .filter(Boolean)
        .sort();
      const earliest = dates.length > 0 ? dates[0] : null;
      const latest = dates.length > 0 ? dates[dates.length - 1] : null;

      setDataOverview({
        totalRecords,
        totalAffiliates: uniqueAffiliates,
        totalSpend,
        totalRevenue,
        dateRange: { earliest, latest },
        isLoading: false,
      });

      console.log("✅ Data overview loaded:", {
        totalRecords,
        totalAffiliates: uniqueAffiliates,
        totalSpend: formatCurrency(totalSpend),
        totalRevenue: formatCurrency(totalRevenue),
        dateRange: `${earliest} to ${latest}`,
      });
    } catch (error) {
      console.error("❌ Error loading data overview:", error);
      setDataOverview((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleFileUpload = useCallback(
    async (event) => {
      const file = event.target.files[0];
      if (!file) return;
      if (brandForCSVUpload === "none") {
        setMessage({
          type: "warning",
          text: "Please select a brand before uploading a CSV.",
        });
        setUploadInitiated(false);
        return;
      }

      setUploadInitiated(true);
      clearMessages();
      clearSupabaseMessages();

      try {
        await new Promise((resolve, reject) => {
          processFile(file, async (csvData) => {
            try {
              await importCSVDataInNewDashboard(csvData, brandForCSVUpload);
              resolve();
            } catch (err) {
              reject(err);
            }
          });
        });
      } catch (err) {
        console.error("Error during file processing or upload:", err);
      }
    },
    [
      processFile,
      importCSVDataInNewDashboard,
      brandForCSVUpload,
      clearMessages,
      clearSupabaseMessages,
    ]
  );

  const csvUploadLoading = csvLoading || supabaseLoading;
  const csvUploadError = csvError || supabaseError;
  const csvUploadSuccess = csvSuccess || supabaseSuccess;

  const handleClearData = async () => {
    setIsLoading(true);
    setMessage({
      type: "info",
      text: "Clearing all data... This might take a moment.",
    });
    // In a real app, you would have a Supabase function to do this securely.
    // const { error } = await supabase.from('your_table').delete().neq('id', 0);
    // For now, we'll just simulate it.
    setTimeout(() => {
      setDataOverview({ total: 0, filtered: 0 });
      setMessage({
        type: "success",
        text: "All data has been cleared successfully.",
      });
      setIsLoading(false);
    }, 2000);
  };

  // Load available dates for date picker
  const loadAvailableDates = async () => {
    try {
      setIsLoadingDates(true);
      const dates = await dateDeleteService.getAvailableDates(50); // Get last 50 dates
      setAvailableDates(dates);
    } catch (error) {
      console.error("Error loading available dates:", error);
      setMessage({
        type: "error",
        text: `Error loading dates: ${error.message}`,
      });
    } finally {
      setIsLoadingDates(false);
    }
  };

  // Handle date selection and show modal
  const handleDateDelete = () => {
    if (!selectedDate) {
      setMessage({ type: "error", text: "Please select a date to delete" });
      return;
    }
    setShowDeleteModal(true);
  };

  // Handle successful deletion
  const handleDeleteComplete = (result) => {
    setMessage({
      type: "success",
      text: `Successfully deleted ${result.deletedCount.toLocaleString()} records for ${dateDeleteService.formatDate(result.date)}`,
    });
    setSelectedDate("");
    // Refresh available dates and data overview
    loadAvailableDates();
    loadDataOverview();
  };

  // Load available dates and data overview when component mounts
  useEffect(() => {
    if (isAdmin) {
      loadAvailableDates();
      loadDataOverview();
    }
  }, [isAdmin]);

  // Refresh data overview when data changes (after uploads or deletions)
  useEffect(() => {
    if (supabaseSuccess || csvSuccess) {
      // Refresh data overview after successful upload
      setTimeout(() => {
        loadDataOverview();
      }, 1000); // Small delay to ensure data is committed
    }
  }, [supabaseSuccess, csvSuccess]);

  // Show loading state while checking admin status
  if (isRoleLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B8460E] mx-auto mb-4"></div>
          <p className="text-gray-300">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Show permission error only after loading is complete
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white p-8 flex items-center justify-center">
        <Card className="text-center p-8 bg-black/40 border border-red-500/50">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-400 mb-6">
            You do not have permission to view this page.
          </p>
          <Link to="/dashboard">
            <Button>Return to Dashboard</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white">
      <Header />
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <header className="mb-8">
            <h1 className="text-4xl font-bold tracking-tight text-white flex items-center gap-3">
              <Settings className="h-8 w-8 text-[#B8460E]" />
              <span>Admin Settings</span>
            </h1>
            <p className="mt-2 text-lg text-gray-400">
              Manage your affiliate dashboard settings and data.
            </p>
          </header>

          {/* Tabs */}
          <div className="mb-8">
            <nav className="flex flex-wrap gap-1 bg-white/5 backdrop-blur-xl rounded-xl p-1 border border-white/10">
              <button
                onClick={() => setActiveTab("upload")}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === "upload"
                    ? "bg-[#B8460E] text-white shadow-lg"
                    : "text-gray-300 hover:text-white hover:bg-white/10"
                }`}
              >
                <Upload className="h-4 w-4" />
                Data Upload
              </button>
              <button
                onClick={() => setActiveTab("whitelist")}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === "whitelist"
                    ? "bg-[#B8460E] text-white shadow-lg"
                    : "text-gray-300 hover:text-white hover:bg-white/10"
                }`}
              >
                <Users className="h-4 w-4" />
                Affiliate Whitelist
              </button>
              <button
                onClick={() => setActiveTab("referrals")}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === "referrals"
                    ? "bg-[#B8460E] text-white shadow-lg"
                    : "text-gray-300 hover:text-white hover:bg-white/10"
                }`}
              >
                <UserPlus className="h-4 w-4" />
                Referral Management
              </button>
              <button
                onClick={() => setActiveTab("modals")}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === "modals"
                    ? "bg-[#B8460E] text-white shadow-lg"
                    : "text-gray-300 hover:text-white hover:bg-white/10"
                }`}
              >
                <MessageSquare className="h-4 w-4" />
                Modal Settings
              </button>
              <button
                onClick={() => setActiveTab("payout")}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === "payout"
                    ? "bg-[#B8460E] text-white shadow-lg"
                    : "text-gray-300 hover:text-white hover:bg-white/10"
                }`}
              >
                <DollarSign className="h-4 w-4" />
                Payout Requests
              </button>
              <button
                onClick={() => setActiveTab("dashboard")}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === "dashboard"
                    ? "bg-[#B8460E] text-white shadow-lg"
                    : "text-gray-300 hover:text-white hover:bg-white/10"
                }`}
              >
                <Wallet className="h-4 w-4" />
                Payout Dashboard
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === "upload" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
              {/* Left Column */}
              <div className="lg:col-span-2 space-y-8">
                <Card className="p-6 bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl">
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                    <Upload className="h-5 w-5 text-[#B8460E]" /> CSV Data
                    Upload
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-start">
                    {/* Left side: Brand Selector */}
                    <div className="space-y-4">
                      <p className="text-gray-300 text-sm">
                        Select a brand and upload the corresponding CSV file to
                        add new performance data to the database.
                      </p>
                      <div className="space-y-2">
                        <label
                          htmlFor="brand-select-csv"
                          className="block text-sm font-semibold text-gray-300"
                        >
                          1. Select Brand
                        </label>
                        <select
                          id="brand-select-csv"
                          value={brandForCSVUpload}
                          onChange={(e) => setBrandForCSVUpload(e.target.value)}
                          className="w-full bg-black/50 border-2 border-white/20 rounded-lg py-3 px-4 text-white focus:ring-2 focus:ring-[#B8460E] focus:border-[#B8460E] appearance-none"
                          disabled={csvUploadLoading}
                        >
                          <option value="none">Select a brand...</option>
                          {BRANDS.map((brand) => (
                            <option key={brand.value} value={brand.value}>
                              {brand.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Right side: File Uploader */}
                    <div className="space-y-4 rounded-xl bg-white/5 p-4 border border-white/10">
                      <div className="text-center">
                        <h4 className="text-lg font-semibold text-white mb-4">
                          2. Choose CSV File
                        </h4>
                        <FileUploader
                          onFileUpload={handleFileUpload}
                          isLoading={csvUploadLoading}
                          disabled={
                            brandForCSVUpload === "none" || csvUploadLoading
                          }
                        />
                        {brandForCSVUpload === "none" && !csvUploadLoading && (
                          <p className="text-xs text-amber-400 mt-4">
                            Please select a brand before uploading a CSV.
                          </p>
                        )}
                      </div>

                      <div className="flex justify-around pt-4">
                        <div className="text-center">
                          <Database className="h-5 w-5 mx-auto text-green-400" />
                          <p className="text-xs font-medium mt-1">
                            Append Data
                          </p>
                          <p className="text-xs text-gray-400">
                            Adds to existing
                          </p>
                        </div>
                        <div className="text-center">
                          <Cog className="h-5 w-5 mx-auto text-blue-400" />
                          <p className="text-xs font-medium mt-1">
                            Auto Processing
                          </p>
                          <p className="text-xs text-gray-400">
                            Affiliate detection
                          </p>
                        </div>
                        <div className="text-center">
                          <Zap className="h-5 w-5 mx-auto text-purple-400" />
                          <p className="text-xs font-medium mt-1">
                            Instant Access
                          </p>
                          <p className="text-xs text-gray-400">
                            Available immediately
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {csvUploadLoading && uploadInitiated && (
                    <div className="mt-6 p-4 bg-black/20 rounded-lg border border-white/10">
                      <ProgressBar
                        progress={importProgress}
                        status={
                          csvUploadError
                            ? "error"
                            : csvUploadSuccess
                              ? "success"
                              : "processing"
                        }
                        message={importMessage}
                        showPercentage={true}
                      />
                    </div>
                  )}

                  {uploadInitiated && !csvUploadLoading && (
                    <div className="mt-6">
                      {csvUploadSuccess && (
                        <div className="flex items-center gap-3 text-green-400 bg-green-500/10 p-3 rounded-lg">
                          <CheckCircle className="h-6 w-6" />
                          <div>
                            <p className="font-semibold">Upload Successful!</p>
                            <p className="text-sm">{csvUploadSuccess}</p>
                          </div>
                        </div>
                      )}
                      {csvUploadError && (
                        <div className="flex items-center gap-3 text-red-400 bg-red-500/10 p-3 rounded-lg">
                          <AlertTriangle className="h-6 w-6" />
                          <div>
                            <p className="font-semibold">Upload Failed</p>
                            <p className="text-sm">{csvUploadError}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                <Card className="p-6 bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl">
                  <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                    <Database className="h-5 w-5 text-[#B8460E]" /> Data
                    Overview
                  </h3>

                  {dataOverview.isLoading ? (
                    <div className="flex items-center space-x-2 text-gray-400">
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#B8460E]"></div>
                      <span>Loading data overview...</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Records and Affiliates */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">
                            Total Records
                          </p>
                          <p className="text-lg font-bold text-white font-mono">
                            {dataOverview.totalRecords.toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">
                            Affiliates
                          </p>
                          <p className="text-lg font-bold text-white font-mono">
                            {dataOverview.totalAffiliates.toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {/* Financial Overview */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/20">
                          <p className="text-xs text-green-400 uppercase tracking-wider mb-1">
                            Total Spend
                          </p>
                          <p className="text-lg font-bold text-green-300 font-mono">
                            {formatCurrency(dataOverview.totalSpend)}
                          </p>
                        </div>
                        <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
                          <p className="text-xs text-blue-400 uppercase tracking-wider mb-1">
                            Total Revenue
                          </p>
                          <p className="text-lg font-bold text-blue-300 font-mono">
                            {formatCurrency(dataOverview.totalRevenue)}
                          </p>
                        </div>
                      </div>

                      {/* ROAS and Date Range */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-orange-500/10 rounded-lg p-3 border border-orange-500/20">
                          <p className="text-xs text-orange-400 uppercase tracking-wider mb-1">
                            Avg ROAS
                          </p>
                          <p className="text-lg font-bold text-orange-300 font-mono">
                            {dataOverview.totalSpend > 0
                              ? (
                                  dataOverview.totalRevenue /
                                  dataOverview.totalSpend
                                ).toFixed(2)
                              : "0.00"}
                          </p>
                        </div>
                        <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/20">
                          <p className="text-xs text-purple-400 uppercase tracking-wider mb-1">
                            Date Range
                          </p>
                          <p className="text-sm font-bold text-purple-300">
                            {dataOverview.dateRange.earliest &&
                            dataOverview.dateRange.latest ? (
                              <>
                                {new Date(
                                  dataOverview.dateRange.earliest
                                ).toLocaleDateString()}
                                <br />
                                <span className="text-xs">to</span>{" "}
                                {new Date(
                                  dataOverview.dateRange.latest
                                ).toLocaleDateString()}
                              </>
                            ) : (
                              "No data"
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Refresh Button */}
                      <div className="pt-2 border-t border-white/10">
                        <button
                          onClick={loadDataOverview}
                          className="w-full px-3 py-2 text-xs bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                          disabled={dataOverview.isLoading}
                        >
                          <Database className="h-3 w-3" />
                          Refresh Data
                        </button>
                      </div>
                    </div>
                  )}
                </Card>

                <Card className="p-6 bg-black/40 backdrop-blur-xl border border-red-500/30 shadow-2xl">
                  <h3 className="text-xl font-semibold text-red-400 mb-4 flex items-center gap-2">
                    <Trash2 className="h-5 w-5" /> Data Management
                  </h3>

                  {/* Delete by Date Section */}
                  <div className="mb-6 p-4 bg-white/5 rounded-xl border border-white/10">
                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-orange-400" />
                      Delete Data by Date
                    </h4>
                    <p className="text-sm text-gray-400 mb-4">
                      Select a specific date to delete all performance data for
                      that day.
                    </p>

                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Select Date
                        </label>
                        <input
                          type="date"
                          value={selectedDate}
                          onChange={(e) => setSelectedDate(e.target.value)}
                          className="w-full bg-gray-800/50 border border-gray-700 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          disabled={isLoadingDates}
                        />
                      </div>
                      <div className="flex-shrink-0 pt-6">
                        <Button
                          onClick={handleDateDelete}
                          disabled={!selectedDate || isLoadingDates}
                          className="bg-orange-600 hover:bg-orange-700 text-white"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Date
                        </Button>
                      </div>
                    </div>

                    {isLoadingDates && (
                      <p className="text-sm text-gray-400">
                        Loading available dates...
                      </p>
                    )}

                    {availableDates.length > 0 && (
                      <div className="text-xs text-gray-500">
                        Available dates: {availableDates.slice(0, 5).join(", ")}
                        {availableDates.length > 5 &&
                          ` and ${availableDates.length - 5} more`}
                      </div>
                    )}
                  </div>

                  {/* Clear All Data Section */}
                  <div className="border-t border-red-500/20 pt-4">
                    <h4 className="text-lg font-semibold text-red-400 mb-2">
                      Clear All Data
                    </h4>
                    <p className="text-sm text-gray-400 mb-4">
                      Permanently delete all performance data from the database.
                      This action cannot be undone.
                    </p>
                    <Button
                      variant="destructive"
                      onClick={handleClearData}
                      disabled={isLoading}
                    >
                      {isLoading ? "Clearing..." : "Clear All Data"}
                    </Button>
                  </div>
                </Card>

                {message.text && (
                  <div
                    className={`p-4 rounded-lg text-sm flex items-center gap-3 ${
                      message.type === "success"
                        ? "bg-green-500/20 text-green-300"
                        : message.type === "error"
                          ? "bg-red-500/20 text-red-300"
                          : message.type === "warning"
                            ? "bg-amber-500/20 text-amber-300"
                            : "bg-blue-500/20 text-blue-300"
                    }`}
                  >
                    <Info className="h-5 w-5" />
                    <span>{message.text}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "whitelist" && (
            <div>
              <WhitelistManager />
            </div>
          )}

          {activeTab === "referrals" && (
            <div>
              <ReferralManager />
            </div>
          )}

          {activeTab === "modals" && (
            <div>
              <ModalSettings />
            </div>
          )}

          {activeTab === "payout" && (
            <div>
              <PayoutRequesters />
            </div>
          )}

          {activeTab === "dashboard" && (
            <div>
              <PayoutDashboard />
            </div>
          )}
        </div>
      </div>

      {/* Delete Date Modal */}
      <DeleteDateModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        selectedDate={selectedDate}
        onDeleteComplete={handleDeleteComplete}
      />
    </div>
  );
};

export default AdminSettingsPage;
